import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ExternalLink, Menu, MoreHorizontal, Settings, X } from 'lucide-react';
import { api, canEdit, canManageUsers, type AuthUser } from '@/lib/api';
import { cn } from '@/lib/utils';
import ServerClockChip from './ServerClockChip';
import PushEnablementPrompt from './PushEnablementPrompt';

type NavLeaf = { to: string; label: string };
type NavGroup = {
  label: string;
  children: NavLeaf[];
  icon?: 'more' | 'gear';
  title?: string;
};
type NavSplit = { to: string; label: string; children: NavLeaf[] };
type NavEntry = NavLeaf | NavGroup | NavSplit;

const isSplit = (e: NavEntry): e is NavSplit => 'children' in e && 'to' in e;
const isGroup = (e: NavEntry): e is NavGroup => 'children' in e && !('to' in e);

const EVENTS_SPLIT_BASE: NavLeaf[] = [
  { to: '/events', label: 'Events' },
  { to: '/events/stats', label: 'Stats' },
];

const MORE_GROUP: NavGroup = {
  label: 'More',
  icon: 'more',
  title: 'More',
  children: [
    { to: '/surveys', label: 'Surveys' },
    { to: '/albums', label: 'Albums' },
  ],
};

function adminGroup(user: AuthUser): NavGroup {
  const hasTransfers = user.alliance?.features.includes('transfers') ?? false;
  const children: NavLeaf[] = [];
  if (user.alliance && canManageUsers(user.role)) {
    children.push(
      { to: '/admin/alliance', label: 'Alliance settings' },
      { to: '/users', label: 'Users' },
      { to: '/admin/alliances', label: 'Alliances' },
      ...(hasTransfers ? [{ to: '/admin/transfers', label: 'Transfer Windows' }] : []),
      { to: '/admin/score-policy', label: 'Scoring' },
      { to: '/admin/phrases', label: 'Phrases' },
      { to: '/admin/worst-parker', label: 'Worst parker' },
      { to: '/admin/hall-of-fame', label: 'Hall of Fame export' },
    );
  } else if (user.isSuperadmin) {
    children.push({ to: '/admin/phrases', label: 'Phrases' });
  }
  if (user.isSuperadmin || user.isServerAdmin) {
    children.push({ to: '/superadmin', label: user.isSuperadmin ? 'Superadmin' : 'Server admin' });
  }
  return { label: 'Administration', icon: 'gear', title: 'Administration', children };
}

export default function Layout({ user, children }: { user: AuthUser; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // ignore network errors — we still want to log out client-side
    }
    qc.removeQueries({ queryKey: ['me'] });
    qc.clear();
    navigate('/login', { replace: true });
  };

  const myHubQuery = useQuery({
    queryKey: ['my-player-link'],
    queryFn: api.myPlayerLink,
    enabled: user.playerId != null,
    staleTime: 5 * 60 * 1000,
  });
  const myHubToken = myHubQuery.data?.token ?? null;

  const alliance = user.alliance;
  const canAdmin = alliance != null && canManageUsers(user.role);
  const hasTransfers = alliance?.features.includes('transfers') ?? false;
  const openWindowsQuery = useQuery({
    queryKey: ['transfer-windows', 'open'],
    queryFn: api.listOpenTransferWindows,
    enabled: canAdmin && hasTransfers,
    staleTime: 60_000,
  });
  const openWindows = openWindowsQuery.data ?? [];
  const transfersLink: NavLeaf | null =
    canAdmin && hasTransfers && openWindows.length > 0
      ? {
          label: openWindows.length === 1 ? 'Transfers' : `Transfers (${openWindows.length})`,
          to:
            openWindows.length === 1
              ? `/admin/transfers/${openWindows[0].id}`
              : '/admin/transfers',
        }
      : null;

  const eventsChildren: NavLeaf[] = [...EVENTS_SPLIT_BASE];
  if (canEdit(user.role)) eventsChildren.push({ to: '/events/vs', label: 'VS' });
  const eventsEntry: NavSplit = {
    to: '/events',
    label: 'Events',
    children: eventsChildren,
  };

  const rosterEntry: NavSplit = {
    to: '/roster',
    label: 'Roster',
    children: [
      { to: '/roster', label: 'Roster' },
      { to: '/roster/self-reports', label: 'Self-Reports' },
      { to: '/roster/access', label: 'Player access' },
      { to: '/roster/chat-bans', label: 'Chat bans' },
      { to: '/strength-requests', label: 'Strength' },
      ...(canEdit(user.role)
        ? [
            { to: '/roster/kill-growth', label: 'Kill growth' },
            { to: '/roster/insights', label: 'Insights' },
          ]
        : []),
    ],
  };

  const admin = adminGroup(user);
  const items: NavEntry[] = alliance
    ? [
        { to: '/dashboard', label: 'Dashboard' },
        rosterEntry,
        eventsEntry,
        { to: '/trains', label: 'Trains' },
        ...(transfersLink ? [transfersLink] : []),
        MORE_GROUP,
        ...(admin.children.length > 0 ? [admin] : []),
      ]
    : admin.children;

  const stopImpersonation = async () => {
    try {
      await api.stopImpersonation();
    } finally {
      qc.clear();
      window.location.assign('/superadmin');
    }
  };

  return (
    <div className="flex min-h-full min-w-0 flex-col">
      {user.impersonator && (
        <div className="flex items-center justify-center gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-slate-950">
          <span>
            Impersonating <b>{user.username}</b>
            {alliance ? ` (${alliance.tag || alliance.name})` : ''} as {user.impersonator.username}
          </span>
          <button
            onClick={stopImpersonation}
            className="rounded border border-slate-950/40 px-2 py-0.5 hover:bg-amber-400"
          >
            Stop
          </button>
        </div>
      )}
      <header className="relative z-40 border-b border-slate-800 bg-slate-900">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-6">
            <Link
              to={alliance ? '/dashboard' : '/superadmin'}
              className="flex min-w-0 items-center gap-2 truncate text-lg font-semibold tracking-tight"
            >
              {alliance?.logoUrl && (
                <img src={alliance.logoUrl} alt="" className="h-7 w-auto shrink-0 rounded" />
              )}
              <span className="truncate">
                {alliance ? alliance.tag || alliance.name : 'Admin'}{' '}
                <span className="text-slate-500">/ Last War</span>
              </span>
            </Link>
            <nav className="hidden gap-1 text-sm md:flex">
              {items.map((it) =>
                isSplit(it) ? (
                  <NavSplitItem key={it.label} entry={it} />
                ) : isGroup(it) ? (
                  <NavDropdown key={it.label} group={it} />
                ) : (
                  <NavItem key={it.to} to={it.to} label={it.label} />
                ),
              )}
            </nav>
          </div>
          <div className="hidden items-center gap-3 text-sm md:flex">
            <ServerClockChip />
            {myHubToken && (
              <a
                href={`/players/${encodeURIComponent(myHubToken)}/self-report`}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-white"
                title="Open my player hub"
              >
                My hub
                <ExternalLink size={12} />
              </a>
            )}
            <Link
              to="/account"
              className="rounded px-2 py-1 text-slate-400 hover:bg-slate-800 hover:text-white"
              title="Account settings"
            >
              {user.username} <span className="text-slate-600">·</span> {user.role}
            </Link>
            <button
              onClick={logout}
              className="rounded border border-slate-700 px-3 py-1 hover:bg-slate-800"
            >
              Log out
            </button>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <ServerClockChip />
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              className="rounded p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-black/50 md:hidden"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <div className="absolute inset-x-0 top-full z-40 border-b border-slate-800 bg-slate-900 shadow-lg md:hidden">
              <nav className="flex flex-col px-2 py-2">
                {items.map((it) =>
                  isSplit(it) ? (
                    <MobileNavSplit key={it.label} entry={it} />
                  ) : isGroup(it) ? (
                    <MobileNavGroup key={it.label} group={it} />
                  ) : (
                    <MobileNavItem key={it.to} to={it.to} label={it.label} />
                  ),
                )}
              </nav>
              {myHubToken && (
                <div className="border-t border-slate-800 px-2 py-2">
                  <a
                    href={`/players/${encodeURIComponent(myHubToken)}/self-report`}
                    className="flex items-center gap-2 rounded px-3 py-2 text-base text-slate-200 hover:bg-slate-800 hover:text-white"
                  >
                    My hub
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-4 py-3 text-sm">
                <Link
                  to="/account"
                  className="min-w-0 truncate rounded px-2 py-1 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  {user.username} <span className="text-slate-600">·</span> {user.role}
                </Link>
                <button
                  onClick={logout}
                  className="shrink-0 rounded border border-slate-700 px-3 py-1 hover:bg-slate-800"
                >
                  Log out
                </button>
              </div>
            </div>
          </>
        )}
      </header>
      <main className="mx-auto w-full min-w-0 max-w-7xl flex-1 overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6">{children}</main>
      <PushEnablementPrompt />
    </div>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'rounded px-2 py-1 transition',
          isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:text-white',
        )
      }
    >
      {label}
    </NavLink>
  );
}

function NavDropdown({ group }: { group: NavGroup }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const active = group.children.some((c) => location.pathname.startsWith(c.to));
  const iconOnly = group.icon != null;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={group.title ?? group.label}
        title={group.title ?? group.label}
        className={cn(
          'flex items-center gap-1 rounded px-2 py-1 transition',
          active || open ? 'bg-slate-800 text-white' : 'text-slate-300 hover:text-white',
        )}
      >
        {group.icon === 'gear' ? (
          <Settings size={16} />
        ) : group.icon === 'more' ? (
          <MoreHorizontal size={16} />
        ) : (
          group.label
        )}
        {!iconOnly && (
          <ChevronDown size={14} className={cn('transition', open && 'rotate-180')} />
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-md border border-slate-800 bg-slate-900 shadow-lg"
        >
          {group.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              role="menuitem"
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  'block px-3 py-2 text-sm transition',
                  isActive
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                )
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NavSplitItem({ entry }: { entry: NavSplit }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const active =
    location.pathname === entry.to ||
    entry.children.some((c) => location.pathname.startsWith(c.to));

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className={cn(
        'group relative flex items-center rounded transition',
        active || open ? 'bg-slate-800 text-white' : 'hover:bg-slate-800',
      )}
    >
      <NavLink
        to={entry.to}
        className={cn(
          'flex items-center rounded-l px-2 py-1 pr-1 transition',
          active ? 'text-white' : 'text-slate-300 group-hover:text-white',
        )}
      >
        {entry.label}
      </NavLink>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${entry.label} menu`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center rounded-r px-1 py-1 transition',
          active || open ? 'text-white' : 'text-slate-400 group-hover:text-white',
        )}
      >
        <ChevronDown size={14} className={cn('transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[9rem] overflow-hidden rounded-md border border-slate-800 bg-slate-900 shadow-lg"
        >
          {entry.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.to === entry.to}
              role="menuitem"
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  'block px-3 py-2 text-sm transition',
                  isActive
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                )
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNavSplit({ entry }: { entry: NavSplit }) {
  return (
    <div className="flex flex-col">
      <MobileNavItem to={entry.to} label={entry.label} />
      <div className="ml-3 flex flex-col border-l border-slate-800 pl-2">
        {entry.children
          .filter((c) => c.to !== entry.to)
          .map((c) => (
            <MobileNavItem key={c.to} to={c.to} label={c.label} />
          ))}
      </div>
    </div>
  );
}

function MobileNavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'rounded px-3 py-2 text-base transition',
          isActive ? 'bg-slate-800 text-white' : 'text-slate-200 hover:bg-slate-800 hover:text-white',
        )
      }
    >
      {label}
    </NavLink>
  );
}

function MobileNavGroup({ group }: { group: NavGroup }) {
  const location = useLocation();
  const activeChild = group.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(activeChild);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center justify-between rounded px-3 py-2 text-base text-slate-200 hover:bg-slate-800 hover:text-white"
      >
        <span>{group.label}</span>
        <ChevronDown size={16} className={cn('transition', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="ml-3 flex flex-col border-l border-slate-800 pl-2">
          {group.children.map((c) => (
            <MobileNavItem key={c.to} to={c.to} label={c.label} />
          ))}
        </div>
      )}
    </div>
  );
}
